import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, LogOut, Download, Loader2, KeyRound, ChevronDown, ChevronUp, AlertTriangle, RefreshCw, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail, BACKEND_URL } from "@/api";
import { AuthProvider, useAuth } from "@/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const LOGO_URL = "/assets/magnetixs-wordmark-white.png";

const euro = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};
const fmtSize = (b) => (b ? `${(b / 1024 / 1024).toFixed(1)} MB` : "–");

const STATUS_FILTERS = [
  { id: "all", label: "Alle Bestellungen" },
  { id: "bezahlt", label: "Nur bezahlte (PayPal)" },
  { id: "offen", label: "Warte auf Zahlung (Vorkasse)" },
];

const AdminLogin = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Anmeldung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#141416" }}>
      <div className="w-full max-w-sm mx-auto px-4">
        <img src={LOGO_URL} alt="Magnetixs" className="h-10 object-contain mx-auto mb-8" />
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-8" data-testid="admin-login-form">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
              <Lock className="w-5 h-5 text-[#E31E24]" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Admin-Anmeldung</h1>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">E-Mail</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email-input"
                className="mt-1 h-12 rounded-lg"
                autoComplete="username"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Passwort</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password-input"
                className="mt-1 h-12 rounded-lg"
                autoComplete="current-password"
              />
            </div>
          </div>
          {error && (
            <p className="text-sm text-red-600 mt-4 flex items-center gap-2" data-testid="admin-login-error">
              <AlertTriangle className="w-4 h-4" /> {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full mt-6 h-12 rounded-full bg-[#E31E24] hover:bg-red-700 text-white font-semibold"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Anmelden"}
          </Button>
        </form>
      </div>
    </div>
  );
};

const ChangePassword = () => {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Passwort geändert. Bitte erneut anmelden.");
      await logout();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Änderung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="toggle-change-password"
        className="flex items-center justify-between w-full text-sm font-semibold text-gray-700"
      >
        <span className="flex items-center gap-2">
          <KeyRound className="w-4 h-4" /> Passwort ändern
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <form onSubmit={submit} className="mt-4 grid sm:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-xs text-gray-500">Aktuelles Passwort</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cur-password" className="mt-1 h-11" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Neues Passwort (min. 10)</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password" className="mt-1 h-11" />
          </div>
          <Button type="submit" disabled={loading} data-testid="change-password-btn" className="h-11 rounded-full bg-[#141416] hover:bg-black text-white">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ändern"}
          </Button>
        </form>
      )}
    </div>
  );
};

const dl = (url) => `${BACKEND_URL}${url}`;

const OrderCard = ({ order, onMarkPaid, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Fresh signed link generated on each click via the admin-protected endpoint (no expiry while panel is open)
  const fileUrl = (kind, position) => `${BACKEND_URL}/api/admin/orders/${order.id}/file/${position}?kind=${kind}`;

  const handleMarkPaid = async (e) => {
    e.stopPropagation();
    setMarkingPaid(true);
    try {
      await onMarkPaid(order.id);
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(order.id);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid={`order-${order.order_number}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50">
        <div>
          <p className="font-bold text-gray-900">{order.order_number}</p>
          <p className="text-sm text-gray-500">
            {order.name} · {order.bundle} Magnete · {euro(order.total)} ·{" "}
            {order.payment === "paypal_card" ? "PayPal & Kartenzahlung" : "Vorkasse / Überweisung"}
          </p>
          <p className="text-xs text-gray-400">{fmtDate(order.created_at)}</p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`text-xs font-semibold px-3 py-1 rounded-full uppercase ${
              order.status === "bezahlt" ? "bg-green-50 text-green-700" : "bg-red-50 text-[#E31E24]"
            }`}
          >
            {order.status}
          </span>
          {order.status !== "bezahlt" && (
            <Button
              onClick={handleMarkPaid}
              disabled={markingPaid}
              data-testid={`mark-paid-btn-${order.order_number}`}
              variant="outline"
              className="h-8 rounded-full border-green-400 text-green-700 hover:bg-green-50"
            >
              {markingPaid ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Zahlung erhalten"}
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                onClick={(e) => e.stopPropagation()}
                data-testid={`delete-order-btn-${order.order_number}`}
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full border-gray-300 text-gray-500 hover:border-red-400 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid={`delete-order-dialog-${order.order_number}`} onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogTitle>Bestellung {order.order_number} wirklich löschen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Möchten Sie diese Bestellung wirklich löschen? Alle Daten und Fotos dieser Bestellung werden
                  unwiderruflich entfernt. Diese Aktion kann nicht zurückgenommen werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid={`delete-order-cancel-btn-${order.order_number}`}>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  data-testid={`delete-order-confirm-btn-${order.order_number}`}
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {deleting ? "Löscht…" : "Endgültig löschen"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5">
          <div className="bg-gray-50 rounded-xl p-4 text-sm mb-4">
            <p className="font-medium">{order.name}</p>
            <p>{order.street}</p>
            <p>{order.postal_code} {order.city}</p>
            <p className="text-gray-500">{order.email}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
            {order.items.map((it) => (
              <div key={it.position} className="border border-gray-200 rounded-xl p-3 self-start" data-testid={`order-item-${order.order_number}-${it.position}`}>
                {it.deleted ? (
                  <div className="w-full bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs text-center p-3" style={{ aspectRatio: it.crop?.orientation === "portrait" ? "56.092/82.021" : "82.021/56.092" }}>
                    Gemäß Aufbewahrungsfrist gelöscht
                  </div>
                ) : (
                  <img
                    src={dl(it.preview_url)}
                    alt={`Motiv ${it.motif_number}`}
                    className="w-full rounded-lg border border-gray-100 object-cover"
                    style={{ aspectRatio: it.crop?.orientation === "portrait" ? "56.092/82.021" : "82.021/56.092" }}
                  />
                )}
                <p className="text-xs font-semibold text-gray-900 mt-2">
                  Motiv {String(it.motif_number ?? (it.position + 1)).padStart(2, "0")} · Menge: {it.quantity ?? 1}×
                </p>
                <p className="text-xs text-gray-500 truncate">{it.filename || "foto"}</p>
                <p className="text-xs text-gray-400">
                  {it.width}×{it.height}px · {fmtSize(it.size_bytes)}
                  {it.low_res && <span className="text-amber-600"> · niedrige Auflösung</span>}
                </p>
                {!it.deleted && (
                  <div className="flex flex-col gap-2 mt-3">
                    <a
                      href={fileUrl("original", it.position)}
                      data-testid={`download-original-${order.order_number}-${it.position}`}
                      className="flex items-center justify-center gap-2 text-xs font-semibold h-9 rounded-full border border-gray-300 hover:border-gray-900 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" /> Original
                    </a>
                    <a
                      href={fileUrl("production", it.position)}
                      data-testid={`download-production-${order.order_number}-${it.position}`}
                      className="flex items-center justify-center gap-2 text-xs font-semibold h-9 rounded-full bg-[#E31E24] hover:bg-red-700 text-white transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" /> Produktionsdatei
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PaypalIssuesBanner = () => {
  const [issues, setIssues] = useState([]);

  const load = useCallback(() => {
    api
      .get("/admin/paypal-issues")
      .then(({ data }) => setIssues(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (id) => {
    try {
      await api.post(`/admin/paypal-issues/${id}/resolve`);
      setIssues((prev) => prev.filter((i) => i.id !== id));
      toast.success("Als erledigt markiert.");
    } catch {
      toast.error("Konnte nicht als erledigt markiert werden.");
    }
  };

  if (issues.length === 0) return null;

  return (
    <div
      className="bg-amber-50 border border-amber-300 rounded-2xl p-5 mb-6"
      data-testid="paypal-issues-banner"
    >
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-5 h-5 text-amber-600" />
        <h2 className="font-bold text-amber-800">Bezahlt, aber keine Bestellung gespeichert ({issues.length})</h2>
      </div>
      <p className="text-sm text-amber-700 mb-4">
        Diese Kunden wurden über PayPal bereits belastet, die Bestellung konnte danach aber nicht gespeichert
        werden. Bitte manuell mit dem Kunden klären und die PayPal-Referenz im PayPal-Dashboard prüfen.
      </p>
      <div className="space-y-2">
        {issues.map((i) => (
          <div key={i.id} className="flex items-center justify-between bg-white rounded-xl p-3 text-sm">
            <div>
              <p className="font-medium text-gray-900">{i.name || "Unbekannt"} · {i.email || "keine E-Mail"}</p>
              <p className="text-gray-500">PayPal-Referenz: {i.paypal_order_id} · {i.bundle ? `${i.bundle} Magnete` : ""} · {fmtDate(i.created_at)}</p>
            </div>
            <Button
              onClick={() => resolve(i.id)}
              data-testid={`resolve-paypal-issue-${i.id}`}
              variant="outline"
              className="h-8 rounded-full border-amber-400 text-amber-700 hover:bg-amber-100"
            >
              Erledigt
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const CleanupOldOrders = ({ onDone }) => {
  const [loading, setLoading] = useState(false);

  const handleCleanup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/orders/cleanup-old");
      toast.success(`Bereinigung gestartet: ${data.pending_count} alte Testbestellung(en) werden im Hintergrund gelöscht. Bitte in 1-2 Minuten "Aktualisieren" klicken.`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          data-testid="cleanup-old-orders-btn"
          variant="outline"
          className="h-9 rounded-full border-red-300 text-red-600 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Alte Testbestellungen löschen
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="cleanup-old-orders-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Alte Testbestellungen endgültig löschen?</AlertDialogTitle>
          <AlertDialogDescription>
            Es werden ausschließlich Bestellungen gelöscht, die explizit als "Test" markiert sind (is_test=True)
            und vor heute erstellt wurden — inklusive ihrer Fotos. Echte Kundenbestellungen werden aus
            gesetzlichen Aufbewahrungsgründen (GoBD) niemals automatisch gelöscht. Diese Aktion kann nicht
            zurückgenommen werden.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cleanup-old-orders-cancel-btn">Abbrechen</AlertDialogCancel>
          <AlertDialogAction
            data-testid="cleanup-old-orders-confirm-btn"
            onClick={handleCleanup}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? "Löscht…" : "Endgültig löschen"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const AdminDashboard = () => {
  const { admin, logout, check } = useAuth();
  const [orders, setOrders] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const loadOrders = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setRefreshing(true);
      try {
        const { data } = await api.get("/admin/orders");
        setOrders(data);
      } catch (e) {
        if (e.response?.status === 401) {
          toast.error("Sitzung abgelaufen – bitte erneut anmelden.");
          check(); // re-validates auth → admin=false → returns to login screen
          return;
        }
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        setOrders((prev) => prev ?? []);
      } finally {
        setRefreshing(false);
      }
    },
    [check]
  );

  useEffect(() => {
    loadOrders({ silent: true });
  }, [loadOrders]);

  // Auto-refresh when the window regains focus (e.g. after processing an order elsewhere).
  useEffect(() => {
    const onFocus = () => loadOrders({ silent: true });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadOrders]);

  const markPaid = async (orderId) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status: "bezahlt" });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: "bezahlt" } : o)));
      toast.success("Als bezahlt markiert.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Status konnte nicht geändert werden.");
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      await api.delete(`/admin/orders/${orderId}`);
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      toast.success("Bestellung gelöscht.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bestellung konnte nicht gelöscht werden.");
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FAF7F2" }}>
      <header className="bg-[#141416] py-4">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Magnetixs" className="h-8 object-contain" />
          <div className="flex items-center gap-4">
            <span className="text-gray-400 text-sm hidden sm:inline" data-testid="admin-email-label">{admin?.email}</span>
            <Button onClick={logout} data-testid="admin-logout-btn" variant="outline" className="h-9 rounded-full border-gray-600 text-white bg-transparent hover:bg-white/10">
              <LogOut className="w-4 h-4 mr-2" /> Abmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Bestellungen</h1>
          <div className="flex items-center gap-2">
            <CleanupOldOrders onDone={() => loadOrders()} />
            <Button
              onClick={() => loadOrders()}
              disabled={refreshing}
              data-testid="refresh-orders-btn"
              variant="outline"
              className="h-9 rounded-full border-gray-300 text-gray-700 hover:border-gray-900"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Aktualisieren
            </Button>
          </div>
        </div>
        <ChangePassword />
        <PaypalIssuesBanner />
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="status-filter-group">
          {STATUS_FILTERS.map((f) => {
            const count =
              orders == null
                ? null
                : f.id === "all"
                ? orders.length
                : f.id === "bezahlt"
                ? orders.filter((o) => o.status === "bezahlt").length
                : orders.filter((o) => o.status !== "bezahlt").length;
            return (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                data-testid={`status-filter-${f.id}`}
                className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${
                  statusFilter === f.id
                    ? "bg-[#141416] text-white"
                    : "bg-white text-gray-600 border border-gray-300 hover:border-gray-900"
                }`}
              >
                {f.label}
                {count !== null && <span data-testid={`status-filter-count-${f.id}`}> ({count})</span>}
              </button>
            );
          })}
        </div>
        {orders === null ? (
          <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /> Lädt…</div>
        ) : (
          (() => {
            const filteredOrders = orders.filter((o) =>
              statusFilter === "all" ? true : statusFilter === "bezahlt" ? o.status === "bezahlt" : o.status !== "bezahlt"
            );
            return filteredOrders.length === 0 ? (
              <p className="text-gray-500" data-testid="no-orders">
                {orders.length === 0 ? "Noch keine Bestellungen vorhanden." : "Keine Bestellungen für diesen Filter."}
              </p>
            ) : (
              <div className="space-y-4" data-testid="orders-list">
                {filteredOrders.map((o) => (
                  <OrderCard key={o.id} order={o} onMarkPaid={markPaid} onDelete={deleteOrder} />
                ))}
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

const AdminInner = () => {
  const { admin } = useAuth();
  if (admin === null)
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#141416" }}>
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  return admin ? <AdminDashboard /> : <AdminLogin />;
};

export default function AdminPage() {
  return (
    <AuthProvider>
      <AdminInner />
    </AuthProvider>
  );
}
